import React from 'react';
import { Rocket } from 'lucide-react';

/**
 * Quiet social-proof badge for creators who made the first-100 cut.
 * Shows a gold pill with "Founding Creator #42 of 100" next to the store
 * name on public store pages.
 *
 * Renders nothing if the creator is not a Founding Creator — safe to drop
 * anywhere without a conditional wrapper.
 *
 * Props:
 *   number: 1–100 (founding_creator_number from /api/creators/store/:slug)
 *   size:   'sm' | 'md' (default 'md')
 */
export function FoundingCreatorBadge({ number, size = 'md' }) {
  if (!number || number < 1 || number > 100) return null;

  const sizing =
    size === 'sm'
      ? 'text-[10px] px-2 py-0.5 gap-1'
      : 'text-xs px-2.5 py-1 gap-1.5';

  return (
    <span
      className={`inline-flex items-center ${sizing} rounded-full bg-gradient-to-r from-[#00F0FF]/20 to-[#00FF94]/10 border border-[#00F0FF]/40 text-[#00F0FF] font-semibold tracking-wide whitespace-nowrap`}
      title={`One of the first 100 creators on theailedger.ai (Founding Creator #${number})`}
      data-testid={`founding-creator-badge-${number}`}
    >
      <Rocket className={size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      Founding Creator #{number}
    </span>
  );
}

/**
 * Helper mirroring the isPartnerSeller(creator) pattern so it reads cleanly
 * in JSX: `{isFoundingCreator(creator) && <FoundingCreatorBadge .../>}`.
 */
export function isFoundingCreator(creator) {
  return Boolean(
    creator?.is_founding_creator &&
      typeof creator?.founding_creator_number === 'number' &&
      creator.founding_creator_number >= 1 &&
      creator.founding_creator_number <= 100
  );
}
